// import { Entry } from '@/models/generics'
import { BaseProduct, CatalogueBaseProduct, CtCatalogueProduct, CtProduct } from '@/models/interfaces/content-type'
import Directr, { ContentType } from '@/services/directr'
import { Entry, RequestHandler, UUID } from 'node_modules/@Protium/vue-ozone-utils/types'
import { computed, ComputedRef, reactive, Ref, WritableComputedRef } from 'vue'
import { SkuController, SkuCtrl } from '../utils'
import useDiffSaveHandler, { SaveHooks } from './useDiffSaveHandler'
import _, { cloneDeep } from 'lodash'
import { $sortBy, uuid } from '@/utils'

export interface ProductListState<P, CP> {
  expanded: Array<string>, // stores expanded sys.ids
  variants: Record<UUID, any>, // stores variants per expanded id
  linkType: ContentType,
  contentType: ContentType,
  modifiedKeys: Array<any>,
  editingItemLink: UUID | null
  search: {
    show: boolean,
    loading: boolean
  },
  productTotal: number,
  products: Array<P>,
  selection: Array<string>,
  removalQueue: Array<string>,
  skus: Array<CP>,
  productQuery: string,
  filterValue: string,
  entryConfig: ComputedRef,
  itemConfig: ComputedRef
}

export interface ProductListContentModuleProps<P> {
  items: WritableComputedRef<Array<P>>,
  requestHandler: Directr,
  contentType: ContentType
}
export interface ProductListHandlers<P,CP> {
  draft?(type:ContentType, product:P, catalogue:Record<string, any>):CP
  saveHooks?:Partial<SaveHooks>
}

export default <P extends BaseProduct = CtProduct, CP extends CatalogueBaseProduct = CtCatalogueProduct>(props, { items, requestHandler, contentType }:ProductListContentModuleProps<CP>, $store, handlers:ProductListHandlers<P,CP> = {saveHooks: {}}, queryFn = (item, chunk) => false) => {
  const state = reactive<ProductListState<P, CP>>({
    linkType: props.linkType || props.contentType,
    contentType,
    expanded: [],
    variants: {},
    modifiedKeys: [],
    editingItemLink: null,
    search: {
      show: false,
      loading: false
    },
    productTotal: 0,
    products: [],
    selection: [],
    removalQueue: [],
    skus: [],
    productQuery: '',
    filterValue: '',
    entryConfig: computed(() => $store.getters['View/EntryConfig']),
    itemConfig: computed(() => $store.getters['View/ItemConfig'])
  })

  function updateProductStore (data: Array<P> | P, total?: number) {
    if (typeof data !== 'object') {
      console.warn('updateProductStore received invalid data', data)
      return
    }
    const items = Array.isArray(data) ? data : [data]
    // console.log('updating cache...', state.products, items)
    items.forEach(item => {
      const exists = (state.products || []).findIndex(n => n.sys.id === item.sys.id)
      if (exists < 0) state.products.push(item as any)
      else state.products.splice(exists, 1, {...state.products[exists], ...item})
    })

    if (total) state.productTotal = total // ease of access...
  }

  function editItemLink (item) {
    // console.log('editItemLink', item)
    state.editingItemLink = item.sys?.id
    openSearch()
  }

  function openSearch () {
    state.search.show = true
    state.search.loading = true

    requestHandler.get(state.linkType, [], { limit: 200 })
      .then(products => {
        updateProductStore(products.items || [], products.sys?.count)

        state.search.loading = true
      }).catch(e => {
        console.error('Error fetching products', e)
      })
  }

  function select (item:P) {
    // console.log('selection', item)
    const index = state.selection.findIndex(s => s === item?.sys?.id)
    if (index >= 0) state.selection.splice(index, 1)
    else state.selection.push(item.sys.id)

    if (!state.products.find(p => p.sys.id === item.sys.id)) state.products.push(item as any) // COMBAK temp fix cast to any
    // console.log('select', state.selection)
  }

  function updateLink () {
    // console.log('updateLink', state.selection)
    const selected = state.selection[0]
    state.selection.length = 0

    if (!state.editingItemLink) {
      console.warn('attempted to update link, but state.editingItemLink was null')
      return
    }

    const index = diffState.rhs.findIndex(n => n.sys.id === state.editingItemLink)
    if (index < 0) {
      console.warn(`attempted to update link, but item by id ${state.editingItemLink} was not found in the current diff`)
      return
    }

    diffState.rhs[index].id = selected as UUID
  }

  // add a new catalogue_product item to the right-hand-side (rhs) list. function @save below handles the item creation etc.
  function add () {
    state.editingItemLink = null // safety clear
    // console.log('add', state.selection)
    // use selection to create new catalogue-product entries
    const selection = state.products.filter(p => state.selection.includes(p.sys.id))
    state.selection.length = 0
    // console.log('product selection', selection)
    // NOTE sku is set right before save is executed to prevent errors
    const drafts:Array<CP> = selection.map(createDraft)
    // prevent dupes
    drafts.forEach(d => {
      if (!diffState.rhs.find(r => r.id === d.id)) diffState.rhs.push(d as any)
    })
  }

  function createDraft (product): CP {
    if (!handlers.draft) throw Error(`Could not create ${contentType} draft. no handler for "draft" was defined`)
    return handlers.draft(contentType, product, props.catalogue)
  }

  function addVariant (entry, id) {
    // console.log('addVariant', entry, id)
    const draft = createDraft(entry)
    diffState.rhs = (diffState.rhs as unknown as Array<CP> || []).concat([draft]) as any // COMBAK temp fix cast to any
  }

  function processRemovalQueue (diffHandlerState, diff) {
    state.removalQueue.forEach((r, i) => {
      if (!r) return state.removalQueue.splice(i, 1)
      const index = diff.rhs.findIndex(f => f.sys?.id === r)
      if (index >= 0) diff.rhs.splice(index, 1, { ...(diff.rhs[index] || {}), sys: { ...(diff.rhs[index].sys || {}), id: (diff.rhs[index] || {}).sys.id, delete: true } }) //
    })

    // clear the queue
    state.removalQueue.length = 0
  }

  // NOTE
  // constructs the display sku from the catalogue id and product sku numeric value
  // logically all this extra information  does not matter for identifying the product, and is stripped in the same manner when an order comes in
  // this keeps the skus extremely flexible to change, and can even be adapted per catalogue when syncing to an external system
  // function displaySku (sku: SKU | string | number) {
  //   return `${props.catalogue.sku_prepend}${$padString((props.catalogue.id ?? '').toString(), SKU_PAD_CHAR, SKU_CAT_PAD_LENGTH)}${props.catalogue.sku_separator}${$padString((sku || '').toString(), SKU_PAD_CHAR, SKU_ID_PAD_LENGTH)}${props.catalogue.sku_append}`
  // }

  function getCatalogueVariantIndex (id:string) {
    return diffState.rhs.findIndex(n => n.id === id)
  }

  function toggleRemoval (item: CP | UUID | undefined) {
    if (!item) { console.warn('toggleRemoval could not toggle item as it was undefined'); return }
    const id:UUID = typeof item === 'object' && 'sys' in item ? item.sys.id : item
    const isDraft = !!(typeof item === 'object' && 'sys' in item && item.sys.draft)

    if (isDraft) {
      const i = diffState.rhs.findIndex(f => f.sys?.id === id)
      diffState.rhs.splice(i, 1)
      return
    }

    const i = state.removalQueue.findIndex(f => f === id)
    if (i >= 0) state.removalQueue.splice(i, 1)
    else state.removalQueue.push(id)

    // also toggle all catalogue_products (in diffstate.rhs) where the catalogue_product.id (product id) = a variant of the input product
    if (Array.isArray(state.variants[(item as CP).id])) {
      state.variants[(item as CP).id].forEach(variantId => toggleRemoval(diffState.rhs.find(f => f && f.id === variantId)))
    }
  }

  // @Prototype - proto for more generic resolver leveraging cache first
  async function resolveWithCache (id:UUID, cache:Array<P>, request:Directr = requestHandler):Promise<P | undefined> {
    // console.log('resolveWithCache', id)

    // check already loaded products list first...
    let product:P = state.products.find(p => p.sys.id === id) as P

    // if nothing, query api
    if (!product) {
      const { items } = await request.get('id', [id])
      product = (items || {})[0]
    }
    // console.log('resolveWithCache resolved', product)
    return product
  }

  async function expandItem (item:CP, event) {
    if (!item || (item && !item.sys)) return
    // console.log('expandItem', item.sys.id)
    const index = state.expanded.findIndex(e => e === item.sys.id)
    if (index >= 0) state.expanded.splice(index, 1)
    else state.expanded.push(item.sys.id)

    if (!item.id) return
    // resolve product id and retrieve variant ids
    const product = await resolveWithCache(item.id, state.products as P[])

    if (product && product.variants) state.variants[item.sys.id] = product.variants.filter(f => !!f || f === 'NULL')
  }

  // lighter filter instead of using composable @useList since this page is already quite beefy
  // quick fix for default filter field selection
  const filterFields = ['title','sku','brand','ean','supplier_product_id']

  function applyQuery (item, i, a) {
    const chunks = (state.productQuery || '').split(',').map(s => s.trim())
    const stockItem = state.products.find(p => p.sys?.id === item?.id)

    return chunks.some(chunk => {
      return filterFields.some(field => {
        return _.deburr(_.kebabCase(item[field] || _.get(stockItem || {}, field, ''))).includes(_.deburr(_.kebabCase(chunk)))
      }) || queryFn(item, chunk)
    })
  }

  function refreshSkuList () {
    if (!props.catalogue) return
    return requestHandler.catalogue.skus(props.catalogue.sys.id)
      .then(({ sys: { total }, items }) => {
        // console.log(`-> loaded ${items?.length} skus. (total ${total})`, items)

        // COMBAK check if the total returned items matches the true total, if not we have a problem...
        // if (total !== items.length) console.warn(`[Skus | Catalogue ${props.catalogue.sys.id}] Total skus did not match the returned total items... Likely some sku's failed to be read, This might cause issues adding new products to the catalogue...`)
        state.skus = saveState.ids = $sortBy(items || [], 'sku').reverse()
        return saveState.ids
      })
  }

  const saveHooks:SaveHooks = handlers.saveHooks || {}

  const {
    save,
    saveState,
    diffState,
    currentDiff,
    registerHook,
    showSaveResult
  } = useDiffSaveHandler<CP>(contentType, requestHandler, saveHooks, [])

  return {
    state,
    applyQuery,
    getCatalogueVariantIndex,
    updateProductStore,
    processRemovalQueue,
    toggleRemoval,
    editItemLink,
    updateLink,
    openSearch,
    add,
    select,
    expandItem,
    addVariant,
    refreshSkuList,
    save,
    saveState,
    diffState,
    currentDiff,
    registerHook,
    showSaveResult
  }
}
